<template>
  <div class="container trip-container">
    <base-nav title="Рейсы">
      <base-button
        v-if="tripStore.trips.length > 0"
        @click="exportToExcel"
      >
        Экспорт в Excel
      </base-button>
      <base-primary-button
        icon="addSquare"
        @click="goToTripCreate"
      >
        Создать
      </base-primary-button>
    </base-nav>

    <!-- Фильтры -->
    <FilterBlock
      ref="filterBlock"
      :search-fields="searchFields"
      :initial-filters="appliedFilters"
      target-type="Trip"
      @filter-change="handleFilterChange"
    />

    <!-- Счетчик результатов -->
    <MetaResultCount
      v-if="
        !tripStore.dataIsLoading &&
        tripStore.trips &&
        tripStore.trips.length > 0
      "
      :pagination-meta="tripStore.pagination"
    />

    <!-- Переключение вида -->
    <TableToolbar
      v-if="!tripStore.dataIsLoading && tripStore.trips.length > 0"
      :show-settings="currentView === 'table'"
      :actions="currentView === 'table' ? dropdownItems : []"
      @open-settings="showSettingsModal = true"
    >
      <base-view-toggle v-model="currentView" />
    </TableToolbar>

    <base-loader v-if="tripStore.dataIsLoading" />

    <!-- Табличный вид -->
    <div
      v-else-if="
        !tripStore.dataIsLoading &&
        tripStore.trips.length > 0 &&
        currentView === 'table'
      "
      class="trip-center"
    >
      <TripTable
        ref="tripTable"
        @selection-change="onSelectionChange"
      />
    </div>

    <!-- Карточный вид -->
    <TripCardList
      v-else-if="
        !tripStore.dataIsLoading &&
        tripStore.trips.length > 0 &&
        currentView === 'cards'
      "
    />

    <div
      class="no-data"
      v-else-if="
        !tripStore.dataIsLoading && tripStore.trips.length == 0
      "
    >
      Список рейсов пуст
    </div>

    <!-- Модальное окно дублирования -->
    <DuplicateModal
      v-if="showDuplicateModal"
      :options="duplicateOptions"
      :initial-selected="initialDuplicateOptions"
      @confirm="handleDuplicateConfirm"
      @close="closeDuplicateModal"
    />

    <!-- Модальное окно настроек таблицы -->
    <Teleport to="body">
      <TableSettingsModal
        v-if="showSettingsModal"
        resource-model="Trip"
        record-type="Trip"
        table-name="trips"
        @close="showSettingsModal = false"
        @config-saved="handleConfigSaved"
      />
    </Teleport>
  </div>
</template>

<script>
import FilterBlock from "@/components/Filter/FilterBlock.vue";
import MetaResultCount from "@/components/Meta/MetaResultCount.vue";
import DuplicateModal from "@/components/DuplicateModal.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";
import TripTable from "@/components/Trip/TripTable.vue";
import TripCardList from "@/components/Trip/TripCardList.vue";

import rawSearchFields from "@/config/filters/trip/trips.json";

import { useTripStore } from "@/stores/tripStore";
import { useNotificationStore } from "@/stores/notificationStore";
import {
  filtersToApiParams,
  getInitialFilters,
  getCurrentPageFromUrl,
  updateUrlWithFilters,
  clearUrlFilters,
} from "@/utils/filterUtils";

export default {
  name: "TripsPage",
  components: {
    FilterBlock,
    MetaResultCount,
    DuplicateModal,
    TableSettingsModal,
    TableToolbar,
    TripTable,
    TripCardList,
  },
  data() {
    return {
      currentView: "table",
      selectedTrips: [],

      appliedFilters: {
        searchValues: {},
        showInactive: false,
      },

      showDuplicateModal: false,
      showSettingsModal: false,

      dropdownItems: [
        {
          id: "duplicate",
          label: "Дублировать",
          action: this.openDuplicateModal,
        },
        {
          id: "enable",
          label: "Показать",
          action: this.enableSelected,
        },
        {
          id: "disable",
          label: "Скрыть",
          isDanger: true,
          action: this.disableSelected,
        },
      ],

      duplicateOptions: [
        {
          id: "include_trip_points",
          label: "Включая точки маршрута",
          description: "Скопировать все точки маршрута рейса",
        },
        {
          id: "include_trip_drivers",
          label: "Включая водителей",
          description: "Скопировать всех связанных водителей",
        },
        {
          id: "include_trip_vehicles",
          label: "Включая транспортные средства",
          description: "Скопировать весь связанный транспорт",
        },
      ],
      initialDuplicateOptions: {
        include_trip_points: true,
        include_trip_drivers: true,
        include_trip_vehicles: true,
      },
    };
  },
  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.getTripsRequest();
      },
    },

    currentView(newView, oldView) {
      if (newView !== oldView) {
        this.selectedTrips = [];
      }
    },
  },
  computed: {
    tripStore() {
      return useTripStore();
    },

    notificationStore() {
      return useNotificationStore();
    },

    searchFields() {
      return rawSearchFields.map((field) => {
        if (!field.optionsFrom) return field;

        const [store, prop] = field.optionsFrom.split(".");
        const { optionsFrom: _optionsFrom, ...rest } = field;

        return {
          ...rest,
          options: this[store]?.[prop] ?? [],
        };
      });
    },
  },
  methods: {
    handleConfigSaved() {
      if (this.$refs.tripTable) {
        this.$refs.tripTable.loadTableConfig();
        this.getTripsRequest();
      }
    },

    async getTripsRequest() {
      const page = getCurrentPageFromUrl(this.$route);
      const params = {
        page: page,
        ...filtersToApiParams(this.appliedFilters),
      };

      let columns = [];
      if (this.$refs.tripTable) {
        columns = this.$refs.tripTable.tableColumns;
      }

      await this.tripStore.fetchTrips(params, columns);
      this.selectedTrips = [];
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        {
          searchValues: {},
          showInactive: false,
        }
      );
    },

    handleFilterChange(filters) {
      this.appliedFilters = { ...filters };

      const hasValues = Object.values(filters.searchValues).some(
        (v) => v !== null && v !== undefined && v !== ""
      );

      if (hasValues || filters.showInactive) {
        updateUrlWithFilters(this.$router, filters, 1);
      } else {
        clearUrlFilters(this.$router);
      }

      this.selectedTrips = [];
    },

    goToTripCreate() {
      this.$router.push({ name: "TripCreate" });
    },

    onSelectionChange(selected) {
      this.selectedTrips = selected;
    },

    async exportToExcel() {
      try {
        const params = filtersToApiParams(this.appliedFilters);
        await this.tripStore.exportTrips(params);
      } catch (error) {
        console.error("Export error", error);
      }
    },

    async enableSelected() {
      if (this.selectedTrips.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы один рейс"
        );
        return;
      }
      try {
        await this.tripStore.enableTrips(this.selectedTrips);
        this.notificationStore.showSuccess(
          `Успешно включено ${this.selectedTrips.length} рейсов`
        );
        await this.getTripsRequest();
      } catch (error) {
        console.error("Enable error", error);
      }
    },

    async disableSelected() {
      if (this.selectedTrips.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы один рейс"
        );
        return;
      }
      try {
        await this.tripStore.disableTrips(this.selectedTrips);
        this.notificationStore.showSuccess(
          `Успешно отключено ${this.selectedTrips.length} рейсов`
        );
        await this.getTripsRequest();
      } catch (error) {
        console.error("Disable error", error);
      }
    },

    openDuplicateModal() {
      if (this.selectedTrips.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы один рейс"
        );
        return;
      }
      this.showDuplicateModal = true;
    },

    closeDuplicateModal() {
      this.showDuplicateModal = false;
    },

    async handleDuplicateConfirm(options) {
      if (this.selectedTrips.length === 0) return;

      try {
        await this.tripStore.duplicateTrips(
          this.selectedTrips,
          options
        );
        this.notificationStore.showSuccess(
          `Успешно продублировано ${this.selectedTrips.length} рейсов`
        );
        await this.getTripsRequest();
        this.closeDuplicateModal();
      } catch (error) {
        console.error("Duplicate error", error);
      }
    },
  },
  async created() {
    this.updateFiltersFromUrl();
  },

  async mounted() {
    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = storedFilters;
      updateUrlWithFilters(this.$router, storedFilters, 1);
    }

    await this.getTripsRequest();
  },
};
</script>
